import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { EnpsComponent } from '@src/suite/common/components/base/enps-component';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';

@Component({
    selector: 'st-assignment-icon-list',
    templateUrl: './assignment-icon-list.html',
    styleUrls: ['./assignment-icon-list.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-assignment-icon-list',
    },
})
export class AssignmentIconListComponent extends EnpsComponent {
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    appService = inject(AppService);

    cardDisabled = input(false);
    disabled = input(false);
    topicEditable = input(true);
    maxItemsToShow = input(5);
    shouldShowAddButton = input(true);
    parentRecord = input<StorytellingTopicRecord>(null);
    value = input<StorytellingAssignmentRecord[]>([]);
    showAddButton = input(true);

    isPanelOpen = signal(false);
    objectType = ObjectTypes.ASSIGNMENT;

    assignmentTypes = computed(() => {
        return this.globalTablesService.getListItemsForField(this.appService.PSTFieldNames.DescriptionType);
    });

    isRTL = computed(() => this.globalTablesService.getRtl());

    extraAssignmentsCount = computed(() => this.value().length - this.maxItemsToShow());
    shouldShowExtraAssignments = computed(() => this.extraAssignmentsCount() > 0);

    i18n = this.store.selectSignal(selectI18N);

    itemsToShow = computed(() => {
        const length = this.shouldShowExtraAssignments() ? this.maxItemsToShow() : this.value().length;
        return this.value().slice(0, length);
    });

    isCompactView = computed(() => this.maxItemsToShow() === 0);
    hasButtonLabel = computed(() => this.value()?.length === 0 && !this.isCompactView());
    addButtonLabel = computed(() => (this.hasButtonLabel() ? `${this.i18n().PSTWebAssignmentAddLabel}` : ''));

    @ViewChild(CdkConnectedOverlay) overlayEl: CdkConnectedOverlay;

    @Output() public itemClicked: EventEmitter<StorytellingAssignmentRecord> = new EventEmitter();
    @Output() public createAssignmentEvent: EventEmitter<string> = new EventEmitter();

    public panelAttached() {
        const overlayElement = this.overlayEl?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isPanelOpen.set(false);
            });
        }
    }

    onMouseOut(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-assignments-overlay')) {
            this.isPanelOpen.set(false);
        }
    }

    openOverlay() {
        if (this.cardDisabled()) return;
        this.isPanelOpen.set(true);
    }

    createAssignment(type: string) {
        this.createAssignmentEvent.emit(type);
    }
}
